import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCalendar, EVENT_CATEGORIES } from '../contexts/CalendarContext';

const FamilyCalendar = () => {
  const { t } = useTranslation();
  const {
    currentMonth,
    selectedDate,
    setSelectedDate,
    setCurrentMonth,
    goToPreviousMonth,
    goToNextMonth,
    goToToday,
    getEventsForDate,
    getEventsForMonth,
    getUpcomingEvents,
    addEvent,
    updateEvent,
    deleteEvent,
    getCategoryColor,
    searchEvents,
    EVENT_CATEGORIES: categories,
    // Google 관련
    googleConnected,
    googleLoading,
    googleError,
    connectGoogle,
    disconnectGoogle,
    refreshGoogleEvents
  } = useCalendar();

  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    date: '',
    time: '',
    category: EVENT_CATEGORIES.FAMILY,
    description: '',
    allDay: true,
    syncToGoogle: false
  });

  // 월의 날짜 배열 생성
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];

    // 이전 달의 날짜들
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }

    // 현재 달의 날짜들
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    // 다음 달의 날짜들 (6주 채우기)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  // 날짜를 로컬 YYYY-MM-DD 형식으로 변환
  const toLocalDateString = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleAddEvent = () => {
    const dateStr = selectedDate
      ? toLocalDateString(selectedDate)
      : toLocalDateString(new Date());

    setEventForm({
      title: '',
      date: dateStr,
      time: '',
      category: EVENT_CATEGORIES.FAMILY,
      description: '',
      allDay: true,
      syncToGoogle: googleConnected
    });
    setEditingEvent(null);
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    setEventForm({
      title: event.title,
      date: event.date,
      time: event.time || '',
      category: event.category,
      description: event.description || '',
      allDay: event.allDay,
      syncToGoogle: event.syncedToGoogle || event.isGoogleEvent || false
    });
    setEditingEvent(event);
    setShowEventModal(true);
  };

  const handleDeleteEvent = (eventId) => {
    if (window.confirm(t('calendar.confirmDelete'))) {
      deleteEvent(eventId);
    }
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim() || !eventForm.date) return;

    const { syncToGoogle, ...eventData } = eventForm;

    if (editingEvent) {
      await updateEvent(editingEvent.id, eventData);
    } else {
      await addEvent(eventData, syncToGoogle);
    }
    setShowEventModal(false);
    setEditingEvent(null);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      [EVENT_CATEGORIES.FAMILY]: t('calendar.categories.family'),
      [EVENT_CATEGORIES.BIRTHDAY]: t('calendar.categories.birthday'),
      [EVENT_CATEGORIES.TRAVEL]: t('calendar.categories.travel'),
      [EVENT_CATEGORIES.HEALTH]: t('calendar.categories.health'),
      [EVENT_CATEGORIES.WORK]: t('calendar.categories.work'),
      [EVENT_CATEGORIES.OTHER]: t('calendar.categories.other')
    };
    return labels[category] || category;
  };

  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
  const days = getDaysInMonth();
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];
  const upcomingEvents = getUpcomingEvents(7);
  const monthEvents = getEventsForMonth(
    currentMonth.getFullYear(),
    currentMonth.getMonth()
  );

  // 검색 결과
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchEvents(searchQuery);
  }, [searchQuery, searchEvents]);

  // 검색 결과에서 일정 클릭 시
  const handleSearchResultClick = (event) => {
    const [year, month, day] = event.date.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day);
    setSelectedDate(eventDate);
    setCurrentMonth(eventDate);
    setShowSearchResults(false);
    setSearchQuery('');
  };

  // 검색 입력 처리
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchResults(value.trim().length > 0);
  };

  // 검색 초기화
  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchResults(false);
  };

  return (
    <div className="max-w-[1200px] mx-auto p-5">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* 캘린더 메인 */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] max-md:p-4">
          {/* 헤더 */}
          <div className="flex justify-between items-center mb-6 max-md:flex-col max-md:gap-3">
            <div className="flex items-center gap-4">
              <button
                onClick={goToPreviousMonth}
                className="w-9 h-9 border-none bg-[#f5f5f7] rounded-full text-xl cursor-pointer transition-all duration-200 flex items-center justify-center hover:bg-[#e5e5e7]"
              >
                ‹
              </button>
              <h2 className="text-2xl max-md:text-xl font-semibold text-[#1d1d1f] m-0 min-w-[180px] text-center">
                {formatMonthYear(currentMonth)}
              </h2>
              <button
                onClick={goToNextMonth}
                className="w-9 h-9 border-none bg-[#f5f5f7] rounded-full text-xl cursor-pointer transition-all duration-200 flex items-center justify-center hover:bg-[#e5e5e7]"
              >
                ›
              </button>
            </div>

            <div className="flex gap-3">
              {/* Google 캘린더 연결 버튼 */}
              {googleConnected ? (
                <div className="flex gap-2 items-center">
                  <button
                    onClick={refreshGoogleEvents}
                    className="w-9 h-9 border border-[#d2d2d7] bg-white rounded-full text-lg cursor-pointer transition-all duration-200 flex items-center justify-center hover:bg-[#f5f5f7] hover:border-[#4285f4] hover:text-[#4285f4] disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={googleLoading}
                    title={t('calendar.google.refresh')}
                  >
                    {googleLoading ? '...' : '↻'}
                  </button>
                  <button
                    onClick={disconnectGoogle}
                    className="flex items-center gap-2 px-4 py-2 border border-[#4285f4] bg-[#e8f0fe] rounded-[980px] text-sm font-medium text-[#4285f4] cursor-pointer transition-all duration-200 hover:bg-[#f5f5f7]"
                    title={t('calendar.google.disconnect')}
                  >
                    <span className="flex items-center justify-center w-[18px] h-[18px] bg-gradient-to-br from-[#4285f4] via-[#34a853] via-[#fbbc05] to-[#ea4335] text-white text-[11px] font-bold rounded-full">
                      G
                    </span>
                    {t('calendar.google.connected')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={connectGoogle}
                  className="flex items-center gap-2 px-4 py-2 border border-[#d2d2d7] bg-white rounded-[980px] text-sm font-medium text-[#1d1d1f] cursor-pointer transition-all duration-200 hover:bg-[#f5f5f7] hover:border-[#4285f4] disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={googleLoading}
                >
                  <span className="flex items-center justify-center w-[18px] h-[18px] bg-gradient-to-br from-[#4285f4] via-[#34a853] via-[#fbbc05] to-[#ea4335] text-white text-[11px] font-bold rounded-full">
                    G
                  </span>
                  {googleLoading ? '...' : t('calendar.google.connect')}
                </button>
              )}
              <button
                onClick={goToToday}
                className="px-4 py-2 border border-[#d2d2d7] bg-white rounded-[980px] text-sm font-medium text-[#1d1d1f] cursor-pointer transition-all duration-200 hover:bg-[#f5f5f7]"
              >
                {t('calendar.today')}
              </button>
              <button
                onClick={handleAddEvent}
                className="px-4 py-2 border-none bg-[#0071e3] rounded-[980px] text-sm font-medium text-white cursor-pointer transition-all duration-200 hover:bg-[#0077ed]"
              >
                + {t('calendar.addEvent')}
              </button>
            </div>
          </div>
          {/* Google 에러 메시지 */}
          {googleError && (
            <div className="bg-[#fff3f3] text-[#ff3b30] px-4 py-3 rounded-[10px] text-sm mb-4">
              {googleError}
            </div>
          )}

          {/* 요일 헤더 */}
          <div className="grid grid-cols-7 mb-2">
            {weekDays.map((day, index) => (
              <div
                key={day}
                className={`text-center py-3 text-[13px] font-semibold ${
                  index === 0 ? 'text-[#ff3b30]' : index === 6 ? 'text-[#0071e3]' : 'text-[#86868b]'
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* 날짜 그리드 */}
          <div className="grid grid-cols-7 gap-px bg-[#e5e5e7] rounded-xl overflow-hidden">
            {days.map((day, index) => {
              const dayEvents = getEventsForDate(day.date);
              const isSunday = day.date.getDay() === 0;
              const isSaturday = day.date.getDay() === 6;

              return (
                <div
                  key={index}
                  className={`bg-white min-h-[90px] max-md:min-h-[70px] p-1.5 max-md:p-1 cursor-pointer transition-colors duration-200 relative flex flex-col overflow-hidden min-w-0
                    ${!day.isCurrentMonth ? 'bg-[#fafafa]' : ''}
                    ${isToday(day.date) ? 'bg-[#f0f7ff]' : ''}
                    ${isSelected(day.date) ? 'bg-[#e8f4ff] shadow-[inset_0_0_0_2px_#0071e3]' : ''}
                    hover:bg-[#f5f5f7]
                  `}
                  onClick={() => handleDateClick(day.date)}
                >
                  <span
                    className={`text-sm max-md:text-xs font-medium
                      ${!day.isCurrentMonth ? 'text-[#c7c7cc]' : 'text-[#1d1d1f]'}
                      ${isSunday && day.isCurrentMonth ? 'text-[#ff3b30]' : ''}
                      ${isSaturday && day.isCurrentMonth ? 'text-[#0071e3]' : ''}
                      ${isToday(day.date) ? 'bg-[#0071e3] text-white rounded-full w-7 h-7 max-md:w-6 max-md:h-6 inline-flex items-center justify-center text-sm max-md:text-xs font-semibold leading-none' : ''}
                    `}
                  >
                    {day.date.getDate()}
                  </span>
                  {dayEvents.length > 0 && (
                    <div className="flex flex-col gap-0.5 mt-1 overflow-hidden min-w-0 w-full">
                      {dayEvents.slice(0, 2).map((event) => (
                        <div
                          key={event.id}
                          className="text-[10px] max-md:text-[9px] text-white px-1 py-0.5 rounded-[3px] whitespace-nowrap overflow-hidden text-ellipsis leading-tight max-w-full block"
                          style={{ backgroundColor: getCategoryColor(event.category, event.isGoogleEvent) }}
                          title={event.title}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <span className="text-[10px] text-[#86868b] pl-0.5">+{dayEvents.length - 2}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 사이드바 */}
        <div className="flex flex-col gap-5 lg:order-none max-lg:order-[-1]">
          {/* 검색 */}
          <div className="relative bg-white rounded-2xl p-4 max-md:p-3 shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
            <div className="flex items-center bg-[#f5f5f7] rounded-[10px] px-3 transition-all duration-200 focus-within:bg-white focus-within:shadow-[0_0_0_2px_#0071e3]">
              <span className="text-sm mr-2 opacity-60">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t('calendar.search.placeholder') || '일정 검색...'}
                className="flex-1 border-none bg-transparent py-2.5 text-sm outline-none min-w-0 w-full placeholder:text-[#86868b]"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="w-5 h-5 border-none bg-[#c7c7cc] text-white rounded-full text-sm cursor-pointer flex items-center justify-center leading-none shrink-0 hover:bg-[#86868b]"
                >
                  ×
                </button>
              )}
            </div>
            {showSearchResults && (
              <div className="bg-white rounded-xl mt-3 max-h-[300px] max-md:max-h-[250px] overflow-y-auto border border-[#e5e5e7]">
                {searchResults.length === 0 ? (
                  <div className="py-5 px-3 text-center text-[#86868b] text-[13px]">
                    {t('calendar.search.noResults') || '검색 결과가 없습니다'}
                  </div>
                ) : (
                  <>
                    <div className="py-2.5 px-3 text-xs text-[#86868b] border-b border-[#e5e5e7] bg-[#f9f9f9]">
                      {searchResults.length}개의 일정
                    </div>
                    {searchResults.slice(0, 10).map((event) => (
                      <div
                        key={event.id}
                        className="flex items-center gap-2.5 py-2.5 px-3 cursor-pointer transition-colors duration-200 hover:bg-[#f5f5f7]"
                        onClick={() => handleSearchResultClick(event)}
                      >
                        <div
                          className="w-[3px] h-7 rounded-sm shrink-0"
                          style={{ backgroundColor: getCategoryColor(event.category, event.isGoogleEvent) }}
                        />
                        <div className="flex-1 min-w-0">
                          <span className="block text-[13px] font-medium text-[#1d1d1f] whitespace-nowrap overflow-hidden text-ellipsis">
                            {event.title}
                          </span>
                          <span className="block text-[11px] text-[#86868b] mt-0.5">
                            {new Date(event.date + 'T00:00:00').toLocaleDateString('ko-KR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    ))}
                    {searchResults.length > 10 && (
                      <div className="py-2.5 px-3 text-center text-[11px] text-[#86868b] border-t border-[#e5e5e7]">
                        +{searchResults.length - 10}개 더 있음
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* 일정 섹션들 (모바일에서 가로 스크롤) */}
          <div className="flex flex-col gap-5 max-lg:flex-row max-lg:overflow-x-auto max-lg:gap-4 max-lg:pb-2 max-lg:[-webkit-overflow-scrolling:touch]">
            {/* 선택된 날짜의 일정 */}
            <div className="bg-white rounded-2xl p-5 shadow-[0_2px_12px_rgba(0,0,0,0.08)] max-lg:min-w-[280px] max-lg:shrink-0">
              <h3 className="text-base font-semibold text-[#1d1d1f] mb-4">
                {selectedDate
                  ? selectedDate.toLocaleDateString('ko-KR', {
                      month: 'long',
                      day: 'numeric',
                      weekday: 'short'
                    })
                  : t('calendar.selectDate')}
              </h3>
              {selectedDateEvents.length === 0 ? (
                <p className="text-[#86868b] text-sm text-center py-5">{t('calendar.noEvents')}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {selectedDateEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`flex items-start gap-3 p-3 rounded-xl transition-colors duration-200 ${
                        event.isGoogleEvent ? 'bg-[#e8f0fe] hover:bg-[#d3e3fd]' : 'bg-[#f5f5f7] hover:bg-[#e5e5e7]'
                      }`}
                    >
                      <div
                        className="w-1 h-full min-h-[40px] rounded-sm shrink-0"
                        style={{ backgroundColor: getCategoryColor(event.category, event.isGoogleEvent) }}
                      />
                      <div className="flex-1 flex flex-col gap-1">
                        <span className="text-sm font-medium text-[#1d1d1f]">
                          {event.title}
                          {event.isGoogleEvent && (
                            <span className="inline-flex items-center justify-center w-4 h-4 bg-[#4285f4] text-white text-[9px] font-bold rounded-full ml-1.5 align-middle">
                              G
                            </span>
                          )}
                        </span>
                        {!event.allDay && event.time && (
                          <span className="text-xs text-[#0071e3]">{event.time}</span>
                        )}
                        <span className="text-[11px] text-[#86868b]">
                          {event.isGoogleEvent ? 'Google Calendar' : getCategoryLabel(event.category)}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {event.isGoogleEvent && event.googleLink && (
                          <a
                            href={event.googleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-7 h-7 border-none bg-transparent rounded-md cursor-pointer text-sm transition-colors duration-200 flex items-center justify-center text-[#4285f4] no-underline hover:bg-[rgba(66,133,244,0.1)]"
                            title={t('calendar.google.openInGoogle')}
                          >
                            ↗
                          </a>
                        )}
                        <button
                          className="w-7 h-7 border-none bg-transparent rounded-md cursor-pointer text-sm transition-colors duration-200 hover:bg-[rgba(0,113,227,0.1)] hover:text-[#0071e3]"
                          onClick={() => handleEditEvent(event)}
                        >
                          ✎
                        </button>
                        <button
                          className="w-7 h-7 border-none bg-transparent rounded-md cursor-pointer text-sm transition-colors duration-200 hover:bg-[rgba(255,59,48,0.1)] hover:text-[#ff3b30]"
                          onClick={() => handleDeleteEvent(event.id)}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 다가오는 일정 */}
            <div className="bg-white rounded-2xl p-5 shadow-[0_2px_12px_rgba(0,0,0,0.08)] max-lg:min-w-[280px] max-lg:shrink-0">
              <h3 className="text-base font-semibold text-[#1d1d1f] mb-4">{t('calendar.upcoming')}</h3>
              {upcomingEvents.length === 0 ? (
                <p className="text-[#86868b] text-sm text-center py-5">{t('calendar.noUpcoming')}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {upcomingEvents.slice(0, 5).map((event) => (
                    <div key={event.id} className="flex items-center gap-3 py-2.5 px-3 bg-[#f5f5f7] rounded-[10px]">
                      <div
                        className="w-1 h-full min-h-[40px] rounded-sm shrink-0"
                        style={{ backgroundColor: getCategoryColor(event.category) }}
                      />
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[13px] font-medium text-[#1d1d1f]">{event.title}</span>
                        <span className="text-[11px] text-[#86868b]">
                          {new Date(event.date).toLocaleDateString('ko-KR', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 이벤트 추가/수정 모달 */}
      {showEventModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5"
          onClick={() => setShowEventModal(false)}
        >
          <div
            className="bg-white rounded-[20px] p-8 max-md:p-6 max-md:rounded-2xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-semibold text-[#1d1d1f] mb-6">
              {editingEvent ? t('calendar.editEvent') : t('calendar.addEvent')}
            </h2>
            <form onSubmit={handleSubmitEvent}>
              <div className="mb-5">
                <label className="block text-sm font-medium text-[#1d1d1f] mb-2">
                  {t('calendar.form.title')}
                </label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder={t('calendar.form.titlePlaceholder')}
                  required
                  className="w-full px-4 py-3 border border-[#d2d2d7] rounded-xl text-base text-[#1d1d1f] bg-white transition-colors duration-200 box-border focus:outline-none focus:border-[#0071e3] focus:shadow-[0_0_0_3px_rgba(0,113,227,0.1)]"
                />
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-[#1d1d1f] mb-2">
                  {t('calendar.form.date')}
                </label>
                <input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-[#d2d2d7] rounded-xl text-base text-[#1d1d1f] bg-white transition-colors duration-200 box-border focus:outline-none focus:border-[#0071e3] focus:shadow-[0_0_0_3px_rgba(0,113,227,0.1)]"
                />
              </div>

              <div className="mb-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={eventForm.allDay}
                    onChange={(e) => setEventForm({ ...eventForm, allDay: e.target.checked })}
                    className="w-[18px] h-[18px] accent-[#0071e3]"
                  />
                  {t('calendar.form.allDay')}
                </label>
              </div>

              {!eventForm.allDay && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-[#1d1d1f] mb-2">
                    {t('calendar.form.time')}
                  </label>
                  <input
                    type="time"
                    value={eventForm.time}
                    onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                    className="w-full px-4 py-3 border border-[#d2d2d7] rounded-xl text-base text-[#1d1d1f] bg-white transition-colors duration-200 box-border focus:outline-none focus:border-[#0071e3] focus:shadow-[0_0_0_3px_rgba(0,113,227,0.1)]"
                  />
                </div>
              )}

              <div className="mb-5">
                <label className="block text-sm font-medium text-[#1d1d1f] mb-2">
                  {t('calendar.form.category')}
                </label>
                <select
                  value={eventForm.category}
                  onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })}
                  className="w-full px-4 py-3 border border-[#d2d2d7] rounded-xl text-base text-[#1d1d1f] bg-white transition-colors duration-200 box-border focus:outline-none focus:border-[#0071e3] focus:shadow-[0_0_0_3px_rgba(0,113,227,0.1)]"
                >
                  {Object.values(categories).map((cat) => (
                    <option key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-[#1d1d1f] mb-2">
                  {t('calendar.form.description')}
                </label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder={t('calendar.form.descriptionPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-3 border border-[#d2d2d7] rounded-xl text-base text-[#1d1d1f] bg-white transition-colors duration-200 box-border resize-y min-h-[80px] focus:outline-none focus:border-[#0071e3] focus:shadow-[0_0_0_3px_rgba(0,113,227,0.1)]"
                />
              </div>

              {/* Google 동기화 옵션 */}
              {googleConnected && !editingEvent?.isGoogleEvent && (
                <div className="mb-5">
                  <label className="flex items-center gap-2 cursor-pointer bg-[#f0f7ff] px-4 py-3 rounded-[10px] border border-[#d3e3fd]">
                    <input
                      type="checkbox"
                      checked={eventForm.syncToGoogle}
                      onChange={(e) => setEventForm({ ...eventForm, syncToGoogle: e.target.checked })}
                      className="w-[18px] h-[18px] accent-[#0071e3]"
                    />
                    <span className="flex items-center justify-center w-4 h-4 bg-gradient-to-br from-[#4285f4] via-[#34a853] via-[#fbbc05] to-[#ea4335] text-white text-[10px] font-bold rounded-full ml-1">
                      G
                    </span>
                    {t('calendar.google.syncToGoogle')}
                  </label>
                </div>
              )}

              {/* Google 이벤트 표시 */}
              {editingEvent?.isGoogleEvent && (
                <div className="flex items-center gap-2 px-4 py-3 bg-[#e8f0fe] rounded-[10px] text-[13px] text-[#4285f4] mb-5">
                  <span className="flex items-center justify-center w-4 h-4 bg-gradient-to-br from-[#4285f4] via-[#34a853] via-[#fbbc05] to-[#ea4335] text-white text-[10px] font-bold rounded-full">
                    G
                  </span>
                  {t('calendar.google.googleEventNotice')}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  className="flex-1 px-6 py-3.5 rounded-xl text-base font-medium cursor-pointer transition-all duration-200 border border-[#d2d2d7] bg-white text-[#1d1d1f] hover:bg-[#f5f5f7]"
                  onClick={() => setShowEventModal(false)}
                >
                  {t('calendar.form.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3.5 rounded-xl text-base font-medium cursor-pointer transition-all duration-200 border-none bg-[#0071e3] text-white hover:bg-[#0077ed]"
                >
                  {editingEvent ? t('calendar.form.update') : t('calendar.form.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyCalendar;
